import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Crown, Trophy, MapPin, Sparkles, ArrowRight, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function initialsOf(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("");
}

function rankAccent(idx) {
  if (idx === 0) return "from-[#F59E0B] to-[#EC4899]";
  if (idx === 1) return "from-[#2563EB] to-[#10B981]";
  if (idx === 2) return "from-[#DC2626] to-[#F59E0B]";
  return "from-white/10 to-white/5";
}

export default function OnlyBestLeaderboardWidget() {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/onlybest/leaderboard?limit=6`);
        if (alive) setCreators(r.data?.creators || []);
      } catch {
        // silently no-op — widget hides if empty
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) return null;

  return (
    <section
      className="py-20 border-t border-white/5 bg-gradient-to-b from-[#070B16] via-[#0B1226] to-[#070B16]"
      data-testid="onlybest-leaderboard-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#F59E0B]/15 to-[#EC4899]/15 border border-[#F59E0B]/30 mb-4">
            <Trophy className="w-4 h-4 text-[#F59E0B]" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#F59E0B]">
              Top OnlyBest Creators
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Outfit'] tracking-tight">
            The creators making it happen.
          </h2>
          <p className="mt-3 text-base sm:text-lg text-[#94A3B8] leading-relaxed">
            Real people promoting the best businesses in their cities. The
            leaderboard re-ranks every day based on content, referrals, and active clients.
          </p>
        </div>

        {creators.length === 0 ? (
          <div
            className="mt-12 max-w-2xl mx-auto rounded-3xl border border-[#F59E0B]/30 bg-gradient-to-br from-[#F59E0B]/10 via-[#0F172A] to-[#EC4899]/10 p-8 text-center"
            data-testid="leaderboard-empty"
          >
            <Sparkles className="w-8 h-8 text-[#F59E0B] mx-auto mb-3" />
            <h3 className="text-white font-semibold text-lg mb-1" data-testid="founding-50-headline">
              Founding 50 — be one of the first OnlyBest Creators.
            </h3>
            <p className="text-sm text-[#94A3B8] leading-relaxed">
              Spots open. The leaderboard goes live as soon as approved creators start recommending businesses.
            </p>
            <Link to="/onlybest/apply" className="inline-block mt-5">
              <Button
                size="lg"
                className="bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:opacity-90 text-white font-semibold rounded-full px-7 py-5"
                data-testid="leaderboard-empty-cta"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Apply — Claim a Founding Spot
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {creators.map((c, idx) => {
                const rank = idx + 1;
                const accent = rankAccent(idx);
                const listingPath = `/business/${c.slug}`;
                const location = [c.city, c.state].filter(Boolean).join(", ");
                return (
                  <Link
                    key={c.id || c.slug}
                    to={listingPath}
                    className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-[#F59E0B]/40 hover:bg-white/[0.05] transition min-w-0"
                    data-testid={`leaderboard-card-${rank}`}
                  >
                    {/* Rank badge */}
                    <div className="absolute -top-3 -left-3">
                      <span
                        className={`inline-flex w-9 h-9 rounded-full bg-gradient-to-br ${accent} items-center justify-center text-white font-bold text-sm shadow-lg`}
                      >
                        #{rank}
                      </span>
                    </div>
                    {c.is_best_of_best && (
                      <div className="absolute top-3 right-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F59E0B]/20 border border-[#F59E0B]/40 text-[10px] uppercase tracking-wider text-[#F59E0B] font-semibold">
                          <Crown className="w-3 h-3" /> Best
                        </span>
                      </div>
                    )}

                    <div className="flex items-center gap-4">
                      {c.logo_url ? (
                        <img
                          src={c.logo_url}
                          alt={`${c.name} avatar`}
                          className="w-14 h-14 rounded-xl object-cover bg-white/5 border border-white/10 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#DC2626] to-[#2563EB] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                          {initialsOf(c.name) || "??"}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="text-white font-semibold text-base leading-tight truncate">
                          {c.name}
                        </h3>
                        <p className="text-xs text-[#94A3B8] truncate">
                          {c.creator_type}
                        </p>
                        {location && (
                          <p className="text-[11px] text-[#64748B] mt-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-[#00E5FF]" /> {location}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                      <Stat label="Posts" value={c.post_count} />
                      <Stat label="Leads" value={c.lead_count} />
                      <Stat
                        label="Score"
                        value={c.score}
                        accent={c.score > 0}
                      />
                    </div>

                    <div className="mt-4 flex items-center justify-between text-xs">
                      <span className="text-[#64748B]">View profile</span>
                      <ArrowRight className="w-3.5 h-3.5 text-[#F59E0B] group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="mt-12 text-center">
              <Link to="/onlybest/apply">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:opacity-90 text-white font-semibold rounded-full px-7 py-6 shadow-lg shadow-[#F59E0B]/30"
                  data-testid="leaderboard-cta-join"
                >
                  <Flame className="w-4 h-4 mr-2" /> Climb the Leaderboard — Apply Free
                </Button>
              </Link>
              <p className="mt-3 text-xs text-[#64748B]">
                Ranking updates daily. More posts, more referrals, more leads = higher score.
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-lg bg-white/[0.04] border border-white/5 px-2 py-2">
      <p
        className={`text-sm font-bold ${
          accent ? "text-[#F59E0B]" : "text-white"
        }`}
      >
        {value ?? 0}
      </p>
      <p className="text-[10px] uppercase tracking-wider text-[#64748B]">{label}</p>
    </div>
  );
}
